import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, ButtonText, HStack, Icon, Pressable, Text, VStack,
} from '@gluestack-ui/themed';
import { ChevronDown, ChevronRight } from 'lucide-react-native';

import type { ThumbsDownReasonsStatus } from './useThumbsDownReasons';
import type { ThumbsDownRowView } from './thumbsDownRowView';

export type ThumbsDownReasonListProps = {
  rows: ThumbsDownRowView[];
  count: number;
  status: ThumbsDownReasonsStatus;
  hasMore?: boolean;
  loadMoreCount?: number;
  isLoadingMore?: boolean;
  loadMoreError?: boolean;
  onLoadMore?: () => void;
  onLoadMoreRetry?: () => void;
  onRetry?: () => void;
  onSelect?: (row: ThumbsDownRowView) => void;
  testID?: string;
};

/**
 * Collapsible list of individual thumbs-down votes (starts collapsed).
 */
export function ThumbsDownReasonList({
  rows,
  count,
  status,
  hasMore = false,
  loadMoreCount = 20,
  isLoadingMore = false,
  loadMoreError = false,
  onLoadMore,
  onLoadMoreRetry,
  onRetry,
  onSelect,
  testID = 'thumbs-down-reasons',
}: ThumbsDownReasonListProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (status === 'ready' && count === 0) {
    return (
      <Box mt="$2" testID={testID}>
        <Text size="sm" color="$textSubtle" testID={`${testID}-empty`}>
          {t('admin.analytics.feedbackHelpfulness.reasonsEmpty')}
        </Text>
      </Box>
    );
  }

  return (
    <Box mt="$2" testID={testID}>
      <Pressable
        onPress={() => setExpanded((open) => !open)}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        testID={`${testID}-toggle`}
      >
        <HStack alignItems="center" space="xs">
          <Icon
            as={expanded ? ChevronDown : ChevronRight}
            size="sm"
            color="$textSubtle"
            testID={`${testID}-chevron`}
          />
          <Text size="sm" color="$textSubtle">
            {expanded
              ? t('admin.analytics.feedbackHelpfulness.reasonsCollapsedHide', { count })
              : t('admin.analytics.feedbackHelpfulness.reasonsCollapsedShow', { count })}
          </Text>
        </HStack>
      </Pressable>
      {!!expanded && (
        <Box mt="$1" testID={`${testID}-list`}>
          {status === 'loading' && (
            <Text size="sm" color="$textSubtle" testID={`${testID}-loading`}>
              {t('admin.analytics.feedbackHelpfulness.reasonsLoading')}
            </Text>
          )}
          {status === 'error' && (
            <VStack space="sm" testID={`${testID}-error`}>
              <Text size="sm" color="$statusError">
                {t('admin.analytics.feedbackHelpfulness.reasonsError')}
              </Text>
              {!!onRetry && (
                <Button size="sm" variant="outline" onPress={onRetry} testID={`${testID}-retry`}>
                  <ButtonText>{t('admin.analytics.feedbackHelpfulness.reasonsRetry')}</ButtonText>
                </Button>
              )}
            </VStack>
          )}
          {status === 'ready' && rows.map((row, index) => (
            <Pressable
              key={row.voteId}
              accessibilityRole="button"
              py="$2"
              borderTopWidth={index === 0 ? 0 : 1}
              borderColor="$borderLight"
              sx={{ _web: { ':hover': { backgroundColor: '$bgHover' } } }}
              testID={`thumbs-down-row-${row.voteId}`}
              onPress={() => onSelect?.(row)}
            >
              <Text size="sm" color="$textBody" numberOfLines={2}>
                {row.reason}
              </Text>
              <Text size="xs" color="$textSubtle" mt="$1">
                {t('admin.analytics.feedbackHelpfulness.reasonRowMeta', {
                  student: row.studentLabel,
                  module: row.moduleLabel,
                  task: row.taskLabel,
                  date: row.dateLabel,
                })}
              </Text>
            </Pressable>
          ))}
          {status === 'ready' && loadMoreError && (
            <VStack space="sm" mt="$2" testID={`${testID}-load-more-error`}>
              <Text size="sm" color="$statusError">
                {t('admin.analytics.feedbackHelpfulness.reasonsError')}
              </Text>
              {!!onLoadMoreRetry && (
                <Button size="xs" variant="outline" onPress={onLoadMoreRetry} testID={`${testID}-load-more-retry`}>
                  <ButtonText>{t('admin.analytics.feedbackHelpfulness.reasonsRetry')}</ButtonText>
                </Button>
              )}
            </VStack>
          )}
          {status === 'ready' && hasMore && !loadMoreError && (
            <Button
              variant="link"
              size="xs"
              alignSelf="flex-start"
              mt="$2"
              onPress={onLoadMore}
              isDisabled={isLoadingMore}
              testID={`${testID}-show-more`}
            >
              <ButtonText>
                {t('admin.analytics.feedbackHelpfulness.reasonsShowMore', { count: loadMoreCount })}
              </ButtonText>
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
